import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { logisticsRequestsApi } from '../../api/logistics';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';
import { formatDate } from '../../utils/formatDate';

export const LOGISTICS_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft',
  clarification_required: 'Additional Information Required', approved: 'Approved', rejected: 'Rejected',
  cancelled: 'Cancelled', in_progress: 'In Progress', completed: 'Completed', closed: 'Closed', archived: 'Archived',
};
const REQUEST_TYPE_LABELS: Record<string, string> = {
  vehicle: 'Vehicle', transport: 'Transport', asset: 'Asset', equipment: 'Equipment', office_supplies: 'Office Supplies',
  programme_supplies: 'Programme Supplies', stock_material: 'Stock/Material', warehouse_dispatch: 'Warehouse Dispatch',
  delivery: 'Delivery', movement_of_goods: 'Movement of Goods', equipment_movement: 'Equipment Movement',
  field_logistics_support: 'Field Logistics Support', other: 'Other',
};
const PRIORITY_LABELS: Record<string, string> = { low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent' };

interface CreateLogisticsForm {
  requesterEmployeeId: number; requestType: string; purpose: string; requiredDate: string; priority?: string; deliveryLocation?: string;
}

export function LogisticsRequestsListPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateLogisticsForm>();

  const { data, isLoading } = useQuery({
    queryKey: ['logistics-requests', statusFilter],
    queryFn: () => logisticsRequestsApi.list({ status: statusFilter || undefined }),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateLogisticsForm) => logisticsRequestsApi.create({ ...input }),
    onSuccess: (result) => {
      showSuccess(`Logistics request ${result.data.requestReference} created.`);
      queryClient.invalidateQueries({ queryKey: ['logistics-requests'] });
      setShowCreateForm(false);
      reset();
      navigate(`/logistics/requests/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const requests = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Logistics Requests" description="Request and track operational logistics support - vehicles, equipment, supplies, and movement of goods." />

      <SectionHeader title="Requests" actions={
        <>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(LOGISTICS_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Logistics Request'}</button>
        </>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Requester <EmployeePicker {...register('requesterEmployeeId', { required: true, valueAsNumber: true })} /></label>
          <label>Request Type
            <select {...register('requestType', { required: true })}>
              {Object.entries(REQUEST_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label>Purpose <input {...register('purpose', { required: true })} /></label>
          <label>Required Date <input type="date" {...register('requiredDate', { required: true })} /></label>
          <label>Priority
            <select {...register('priority')}>
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label>Delivery Location <input {...register('deliveryLocation')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Create Draft</button>
        </form>
      )}

      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={requests}
          rowKey={(r) => r.id}
          columns={[
            { header: 'Reference', render: (r) => <a onClick={() => navigate(`/logistics/requests/${r.id}`)} className="table-link">{r.request_reference}</a> },
            { header: 'Type', render: (r) => REQUEST_TYPE_LABELS[r.request_type] ?? r.request_type },
            { header: 'Purpose', render: (r) => r.purpose },
            { header: 'Required Date', render: (r) => formatDate(r.required_date) },
            { header: 'Priority', render: (r) => PRIORITY_LABELS[r.priority] ?? r.priority },
            { header: 'Status', render: (r) => <StatusBadge status={r.status} label={LOGISTICS_STATUS_LABELS[r.status]} /> },
          ]}
        />
      )}
    </div>
  );
}
